import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { site } from "../content/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name (at least 2 characters)."),
  email: z.string().trim().email("Please enter a valid email address."),
  message: z.string().trim().min(10, "Please tell us a little more (at least 10 characters)."),
});

const inputClass =
  "w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500";

export const Contact = () => {
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState("");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (values) => {
    setSendError("");
    setSent(false);
    try {
      await axios.post(`${API}/contact`, values);
      setSent(true);
      reset();
    } catch (e) {
      setSendError(
        e.response?.data?.detail ||
          "Your message could not be sent right now. Please email us directly instead."
      );
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-28 bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-12 gap-10 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-teal-700 bg-teal-50 border border-teal-200/60 px-3 py-1.5 rounded-full inline-block">
              {site.contact.eyebrow}
            </p>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {site.contact.heading}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{site.contact.sub}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={`mailto:${site.email}`}
              data-testid="contact-direct-email-link"
              className="mt-8 flex items-center gap-4 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-300 rounded-2xl p-5 transition-colors duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
            >
              <span className="w-11 h-11 rounded-xl bg-white border border-slate-200 text-teal-600 flex items-center justify-center shrink-0">
                <Mail size={19} />
              </span>
              <span>
                <span className="block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                  Prefer email directly?
                </span>
                <span className="block mt-1 font-semibold text-slate-900 group-hover:text-teal-700 transition-colors break-all">
                  {site.email}
                </span>
              </span>
            </a>
          </Reveal>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <Reveal delay={0.08}>
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-semibold text-slate-800 mb-2">
                    Full Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    placeholder="Jane Doe"
                    autoComplete="name"
                    data-testid="contact-input-name"
                    aria-invalid={!!errors.name}
                    className={inputClass}
                    {...register("name")}
                  />
                  {errors.name && (
                    <p data-testid="contact-error-name" role="alert" className="mt-2 text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-semibold text-slate-800 mb-2">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    placeholder="jane@company.com"
                    autoComplete="email"
                    data-testid="contact-input-email"
                    aria-invalid={!!errors.email}
                    className={inputClass}
                    {...register("email")}
                  />
                  {errors.email && (
                    <p data-testid="contact-error-email" role="alert" className="mt-2 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="contact-message" className="block text-sm font-semibold text-slate-800 mb-2">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  rows={6}
                  placeholder="Tell us about your workflow challenges or inquiry..."
                  data-testid="contact-input-message"
                  aria-invalid={!!errors.message}
                  className={`${inputClass} resize-y min-h-[140px]`}
                  {...register("message")}
                />
                {errors.message && (
                  <p data-testid="contact-error-message" role="alert" className="mt-2 text-sm text-red-600">
                    {errors.message.message}
                  </p>
                )}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-7 py-3.5 rounded-full shadow-sm shadow-teal-600/25 transition-all duration-200 hover:-translate-y-0.5 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
                >
                  {isSubmitting ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
                {sent && (
                  <p data-testid="contact-success-message" role="status" className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700">
                    <CheckCircle2 size={17} />
                    Message sent — it is on its way to our inbox.
                  </p>
                )}
                {sendError && (
                  <p data-testid="contact-send-error" role="alert" className="text-sm font-medium text-red-600">
                    {sendError}{" "}
                    <a href={`mailto:${site.email}`} className="underline">
                      {site.email}
                    </a>
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
